import { useQueries } from "@tanstack/react-query"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { Chip, Tooltip } from "@atoms/index"

import { barcodeLookupQueryOptions } from "@pages/Pricebook/components/CodeScannerDialog/useBarcodeLookupQuery"

import { PRICEBOOK_ENDPOINTS } from "@endpoints/pricebook"

import styles from "./BarcodeSuggestions.module.scss"
import { BarcodeSuggestion, BarcodeSuggestionsProps } from "./BarcodeSuggestions.types"

// One suggestion per distinct code among the connected catalog items, naming every catalog that carries it
function collectSuggestions(catalogLinks: PriceBookItemCatalogLink[]): BarcodeSuggestion[] {
    const suggestions: BarcodeSuggestion[] = []

    for (const link of catalogLinks) {
        const code = link.catalog_item.barcode

        if (link.is_connected && code !== "") {
            const existing = suggestions.find((suggestion) => suggestion.code === code)

            if (existing === undefined) {
                suggestions.push({ code, catalogNames: [link.catalog.name] })
            } else if (!existing.catalogNames.includes(link.catalog.name)) {
                existing.catalogNames.push(link.catalog.name)
            }
        }
    }

    return suggestions
}

// The codes a part can take from its connected catalog items, offered as chips under the barcode field
function BarcodeSuggestions(props: BarcodeSuggestionsProps) {
    const { catalogLinks, onSelect, pricebookItemId, isDisabled = false } = props
    const suggestions = collectSuggestions(catalogLinks)

    // A code another part already holds can't be registered here, so its chip only says where it went. The item's
    // own saved code (the field was just cleared) stays free.
    const { user } = useUser()
    const [searchEndpointTemplate] = useSlugExtractor([PRICEBOOK_ENDPOINTS.SEARCH])
    const owners = useQueries({
        queries: suggestions.map((suggestion) =>
            barcodeLookupQueryOptions(searchEndpointTemplate, suggestion.code, !!user?.service_company?.slug),
        ),
    })

    if (suggestions.length === 0) {
        return null
    }

    return (
        <div className={styles.chips}>
            {suggestions.map((suggestion, index) => {
                const owner = owners[index].data ?? null
                const otherOwner = owner !== null && owner.id !== pricebookItemId ? owner : null

                return (
                    <Tooltip
                        key={suggestion.code}
                        body={
                            <div className={styles.tooltip}>
                                {suggestion.catalogNames.map((catalogName) => (
                                    <span key={catalogName} className={styles.catalog}>
                                        {catalogName}
                                    </span>
                                ))}
                                <span className={styles.code}>{suggestion.code}</span>
                                {owner !== null && (
                                    <span className={styles.registered}>
                                        Already registered to {owner.description}
                                    </span>
                                )}
                            </div>
                        }
                    >
                        <Chip
                            type="button"
                            className={styles.chip}
                            onClick={() => onSelect(suggestion.code)}
                            isDisabled={isDisabled || otherOwner !== null}
                            isLoading={owners[index].isLoading}
                            noDebounce={true}
                        >
                            {suggestion.code}
                        </Chip>
                    </Tooltip>
                )
            })}
        </div>
    )
}

export default BarcodeSuggestions
